// Página de perfil del usuario autenticado: editar datos personales + cambiar contraseña.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/services/api';
import {
  changePassword,
  fetchMeDetail,
  updateProfile,
  type AuthUserDetail,
} from '@/services/auth';
import styles from './Perfil.module.scss';

export function Perfil() {
  const { refreshUser } = useAuth();
  const [me, setMe] = useState<AuthUserDetail | null>(null);
  const [loading, setLoading] = useState(true);

  // Profile form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Password form state
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdMsg, setPwdMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const u = await fetchMeDetail();
      setMe(u);
      setFirstName(u.firstName);
      setLastName(u.lastName);
      setDisplayName(u.displayName);
      setEmail(u.email);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    setProfileMsg(null);
    setSavingProfile(true);
    try {
      await updateProfile({
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        display_name: displayName.trim() || undefined,
        email: email.trim() || undefined,
      });
      await refreshUser();
      setProfileMsg({ ok: true, text: 'Perfil actualizado.' });
    } catch (e) {
      const msg = e instanceof ApiError && e.payload && typeof e.payload === 'object' && 'code' in e.payload && e.payload.code === 'email_taken'
        ? 'Ese email ya está en uso por otro usuario.'
        : e instanceof ApiError ? e.message : 'Error al guardar';
      setProfileMsg({ ok: false, text: msg });
    } finally {
      setSavingProfile(false);
    }
  }

  async function savePassword(e: FormEvent) {
    e.preventDefault();
    setPwdMsg(null);
    if (newPwd !== confirmPwd) { setPwdMsg({ ok: false, text: 'Las contraseñas no coinciden.' }); return; }
    if (newPwd.length < 8) { setPwdMsg({ ok: false, text: 'La contraseña debe tener al menos 8 caracteres.' }); return; }
    setSavingPwd(true);
    try {
      await changePassword(currentPwd, newPwd);
      setPwdMsg({ ok: true, text: 'Contraseña actualizada.' });
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (e) {
      const msg = e instanceof ApiError && e.payload && typeof e.payload === 'object' && 'code' in e.payload && e.payload.code === 'wrong_password'
        ? 'La contraseña actual no es correcta.'
        : e instanceof ApiError ? e.message : 'Error al cambiar';
      setPwdMsg({ ok: false, text: msg });
    } finally {
      setSavingPwd(false);
    }
  }

  return (
    <>
      <Header title="Mi perfil" />
      <main className={styles.main}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner size="lg" /></div>
        ) : !me ? null : (
          <>
            <div className={styles.card}>
              <h2>Datos personales</h2>
              <div className={styles.meta}>
                {me.roles.map((r) => <Badge key={r} tone="accent">{r}</Badge>)}
              </div>

              <form onSubmit={saveProfile}>
                <div className={styles.row2}>
                  <label className={styles.field}>
                    <span>Nombre</span>
                    <input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={100} />
                  </label>
                  <label className={styles.field}>
                    <span>Apellidos</span>
                    <input value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={100} />
                  </label>
                </div>
                <label className={styles.field}>
                  <span>Nombre visible</span>
                  <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={200} />
                </label>
                <label className={styles.field}>
                  <span>Email</span>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} />
                </label>

                {profileMsg && (
                  <div className={`${styles.feedback} ${profileMsg.ok ? styles.feedbackOk : styles.feedbackErr}`}>
                    {profileMsg.text}
                  </div>
                )}

                <div className={styles.actions}>
                  <Button type="submit" variant="primary" disabled={savingProfile}>
                    {savingProfile ? 'Guardando…' : 'Guardar cambios'}
                  </Button>
                </div>
              </form>
            </div>

            <div className={styles.card}>
              <h2>Cambiar contraseña</h2>
              <form onSubmit={savePassword}>
                <label className={styles.field}>
                  <span>Contraseña actual</span>
                  <input type="password" autoComplete="current-password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} required />
                </label>
                <div className={styles.row2}>
                  <label className={styles.field}>
                    <span>Nueva contraseña</span>
                    <input type="password" autoComplete="new-password" minLength={8} value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required />
                  </label>
                  <label className={styles.field}>
                    <span>Repetir nueva</span>
                    <input type="password" autoComplete="new-password" minLength={8} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required />
                  </label>
                </div>

                {pwdMsg && (
                  <div className={`${styles.feedback} ${pwdMsg.ok ? styles.feedbackOk : styles.feedbackErr}`}>
                    {pwdMsg.text}
                  </div>
                )}

                <div className={styles.actions}>
                  <Button type="submit" variant="primary" disabled={savingPwd}>
                    {savingPwd ? 'Cambiando…' : 'Cambiar contraseña'}
                  </Button>
                </div>
              </form>
            </div>
          </>
        )}
      </main>
    </>
  );
}
